// src/components/SlotStoryView.jsx
//
// Slot Story - the default ad-QA view (docs/slot-story-spec.md Contract D).
// One row per ad slot with a Prebid -> APS -> GAM -> Winner -> Impression chip
// pipeline and a plain-language verdict. Expanding a slot shows the evidence
// behind every chip and a button that opens the request it came from.
//
// Data: the SAME server-first lifecycle graph the sign-off report uses
// (postLifecycleGroups, newest-arrived-answer-wins), local fallback behind
// resolveGraphSource, with the provenance shown. No new fetch path.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { postLifecycleGroups } from '../api/lifecycle';
import { useLatestWins } from '../utils/latestWins';
import {
  buildSlotLifecycles,
  groupLifecyclesBySlot,
  resolveGraphSource,
  detectRedundancies,
  RedundancyPopover,
  OrphanPopover,
  SDKPanel,
  buildDisplayLookupKeys,
} from './AdLifecycleView';
import useCoalescedPost from '../utils/useCoalescedPost';
import SessionQualityAlert from './SessionQualityAlert';
import OmidSessionsPanel from './OmidSessionsPanel';
import { ConfidenceChip, LinkConfidenceRow } from './LinkConfidence';
import MediationBuyers from './MediationBuyers';
import { lifecycleConfidence } from '../utils/linkConfidence';
import buildOrphanReport from '../validation/orphanDetection';
import { reconstructOmidSessions } from '../validation/omidLifecycle';
import { buildSlotStory, buildSlotTimeline, summarizeSession } from '../utils/slotStory';
import { activeViewRollup } from '../validation/activeView';
import { buildSlotTicket } from '../utils/slotTicket';
import { buildSlotHar, slotRowKeys, isLogcatLifecycle } from '../utils/slotHar';
import { rewriteLinesForSlot, logcatRewriteNotice, REWRITE_LABEL } from '../utils/slotRewrite';
import useIuRules from '../utils/useIuRules';
import { billDuplicatesForLifecycle } from '../utils/mergeBillingPings';
import { downloadHarFile } from '../utils/evidenceHar';
import { copyTextToClipboard } from '../utils/clipboard';
import SessionOverview, { shortPage, buildExpectationMisses } from './SessionOverview';
import { runChecks } from '../api/checks';
import { findPackByIu, getPacks } from '../api/appPacks';
import { diagnoseNoGam, NO_GAM_ADVICE, partialGamCapture } from '../utils/noGamDiagnosis';

const RANK = { fail: 0, warn: 1, review: 2, pass: 3 };
const rankOf = (s) => (RANK[s] != null ? RANK[s] : 3);

const STATE_STYLE = {
  ok: { bg: 'var(--success-bg)', fg: 'var(--success-text)', bd: 'var(--success-border)', icon: '✓' },
  warn: { bg: 'var(--warn-bg)', fg: 'var(--warn-text)', bd: 'var(--warn-border)', icon: '!' },
  fail: { bg: 'var(--error-bg)', fg: 'var(--error-text)', bd: 'var(--error-border)', icon: '✗' },
  unknown: { bg: 'var(--neutral-bg)', fg: 'var(--muted)', bd: 'var(--neutral-border)', icon: '?' },
  absent: { bg: 'transparent', fg: 'var(--muted)', bd: 'var(--line)', icon: '–', dashed: true },
};
const FILL_STYLE = {
  filled: { bg: 'var(--success-bg)', fg: 'var(--success-text)', bd: 'var(--success-border)' },
  nofill: { bg: 'var(--error-bg)', fg: 'var(--error-text)', bd: 'var(--error-border)' },
  unknown: { bg: 'var(--neutral-bg)', fg: 'var(--muted)', bd: 'var(--neutral-border)' },
};

function FillFlag({ fill }) {
  if (!fill) return null;
  const st = FILL_STYLE[fill.state] || FILL_STYLE.unknown;
  return (
    <span
      data-testid="slot-fill"
      data-fill={fill.state}
      title={fill.why.join('\n')}
      style={{ fontSize: 10, fontWeight: 700, padding: '0 6px', borderRadius: 4, whiteSpace: 'nowrap', background: st.bg, color: st.fg, border: `1px solid ${st.bd}` }}
    >
      {fill.label}
    </span>
  );
}

const DOT = { fail: 'var(--error-text)', warn: 'var(--warn-text)', review: 'var(--rec-text)', pass: 'var(--success-text)' };

const STATE_WORD = {
  ok: 'OK',
  warn: 'Warning',
  fail: 'Failed',
  unknown: 'Not visible in this capture',
  absent: 'Not seen',
};

const tsOf = (v) => (typeof v === 'number' ? v : Date.parse(v) || 0);
const pad = (n, w = 2) => String(n).padStart(w, '0');
/** Clock time of the slot's GAM request, ms precision (slots load within one second). */
export function fmtClock(v) {
  const t = tsOf(v);
  if (!t) return '';
  const d = new Date(t);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function pageKeyOf(group, slot) {
  const page = group.pageName || slot.pageUrl || group.pageUrl || '';
  return `${slot.device || ''}\u0001${group.visitLabel || ''}\u0001${page}`;
}

// Device tab of each row (`_deviceTab`, All-devices scope), in first-seen order.
function splitByDevice(rows) {
  const m = new Map();
  for (const r of rows) {
    const k = (r && r._deviceTab) || '';
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(r);
  }
  return m;
}
const tagDevice = (lc, device) => (device ? { ...lc, device } : lc);

/**
 * Build the graph one device at a time and merge. Lifecycle matching pairs
 * impressions and billing notices by timing, so two phones' rows in one build
 * would cross-attribute. `build(rows)` returns a graph or a promise of one.
 */
export function mergeDeviceGraphs(parts) {
  const out = { lifecycles: [], groups: [], redundancies: [] };
  const reports = [];
  let omid = null;
  for (const [device, g] of parts) {
    if (!g) continue;
    const tag = (lc) => tagDevice(lc, device);
    out.lifecycles.push(...(g.lifecycles || []).map(tag));
    out.groups.push(...(g.groups || []).map((grp) => ({ ...grp, device: device || undefined, slots: (grp.slots || []).map(tag) })));
    out.redundancies.push(...(g.redundancies || []));
    if (g.orphanReport) reports.push(g.orphanReport);
    // Only claim server OMID when every device answered with it; otherwise the
    // caller reconstructs it locally rather than showing half the sessions.
    if (Array.isArray(g.omidSessions)) omid = omid === false ? false : [...(omid || []), ...g.omidSessions];
    else omid = false;
  }
  if (reports.length) out.orphanReport = mergeOrphanReports(reports);
  if (Array.isArray(omid)) out.omidSessions = omid;
  return out;
}

/** One orphan report from several (per device). */
export function mergeOrphanReports(reports) {
  const out = { orphans: [], duplicates: [], counts: { total: 0, claimed: 0, orphans: 0, duplicates: 0 }, reasons: [] };
  for (const r of reports || []) {
    if (!r) continue;
    out.orphans.push(...(r.orphans || []));
    out.duplicates.push(...(r.duplicates || []));
    out.reasons.push(...(r.reasons || []));
    for (const k of Object.keys(out.counts)) out.counts[k] += Number(r.counts && r.counts[k]) || 0;
  }
  return out;
}

/** Flatten server/local groups into page/visit buckets of slot lifecycles. */
export function bucketByPage(groups, rowsByKey, activeViewRollup, redundancies) {
  const map = new Map();
  for (const g of groups || []) {
    for (const lc of g.slots || []) {
      const k = pageKeyOf(g, lc);
      if (!map.has(k)) {
        map.set(k, {
          key: k,
          visitLabel: g.visitLabel || null,
          device: lc.device || null,
          page: g.pageName || lc.pageUrl || g.pageUrl || null,
          slots: [],
        });
      }
      map.get(k).slots.push(lc);
    }
  }
  const pages = Array.from(map.values()).map((p) => {
    // rowsByKey (optional) lets the story state N identical billing pings (WP3).
    // activeViewRollup (optional) lets the Viewability step say how many pings could not be attributed.
    // redundancies (optional) are the session's redundancy findings; those naming a slot raise its verdict.
    const stories = p.slots.map((lc) => {
      const o = {};
      if (rowsByKey) o.duplicateBills = billDuplicatesForLifecycle(lc, rowsByKey);
      if (activeViewRollup) o.activeViewRollup = activeViewRollup;
      if (Array.isArray(redundancies) && redundancies.length) o.redundancies = redundancies;
      return { lc, story: buildSlotStory(lc, Object.keys(o).length ? o : undefined) };
    });
    // newest first — QA watches the latest request land; status shows as the dot colour
    stories.sort((a, b) => tsOf(b.lc.timestamp) - tsOf(a.lc.timestamp));
    const status = stories.reduce((w, s) => (rankOf(s.story.status) < rankOf(w) ? s.story.status : w), 'pass');
    const latest = stories.reduce((m, s) => Math.max(m, tsOf(s.lc.timestamp)), 0);
    return { ...p, stories, status, latest };
  });
  pages.sort((a, b) => b.latest - a.latest);
  return pages;
}

const REDUNDANCY_TONE = {
  fail: { background: 'var(--error-bg)', color: 'var(--error-text)', border: '1px solid var(--error-border)' },
  warn: { background: 'var(--warn-bg)', color: 'var(--warn-text)', border: '1px solid var(--warn-border)' },
  review: { background: 'var(--info-bg)', color: 'var(--info-text)', border: '1px solid var(--info-border)' },
};

// The lifecycle grouper's repeat-request classification of this GAM request (lifecycle refreshFlag,
// set by the group classifier) - "refresh" alone hid duplicates, early reloads and floods.
const REFRESH_TAG = {
  duplicate: { text: 'duplicate', tone: 'var(--error-text)', tip: 'Same ad unit and context requested again within the duplicate window' },
  rapid: { text: 'early reload', tone: 'var(--warn-text)', tip: 'Re-requested before the minimum refresh interval (not a refresh)' },
  flood: { text: 'flood', tone: 'var(--warn-text)', tip: 'Runaway repeat requests for this ad unit' },
  scroll: { text: 'scroll', tone: 'var(--muted)', tip: 'Infinite-scroll feed unit recycled' },
  gallery: { text: 'gallery', tone: 'var(--muted)', tip: 'Gallery unit requested per item' },
  refresh: { text: 'refresh', tone: 'var(--muted)', tip: 'Refresh after the minimum interval' },
};
const fmtGap = (ms) => (ms == null ? '' : ms < 1000 ? ` +${Math.round(ms)}ms` : ` +${(ms / 1000).toFixed(1)}s`);
function RefreshTag({ lc }) {
  const t = REFRESH_TAG[lc && lc.refreshFlag];
  if (!t) return lc && lc.isRefresh ? <span style={{ fontSize: 10, color: 'var(--muted)' }}>refresh</span> : null;
  return (
    <span data-testid="slot-refresh" title={t.tip} style={{ fontSize: 10, color: t.tone, whiteSpace: 'nowrap' }}>
      {t.text}{fmtGap(lc.refreshGapMs)}
    </span>
  );
}

// Shown on the Viewable chip of a USB slot (copy: docs/sdlc/slot-story-complete "UI copy for unsupported paths").
export const VIEWABLE_USB_TIP = 'OMID viewability is not visible in USB capture. Use a MITM-captured session to verify.';
// When the proxy evaluated Google ActiveView too (lifecycle.activeView present) the tip names both.
export const VIEWABLE_USB_TIP_AV = 'Google ActiveView and OMID viewability are not visible in USB capture. Use a MITM-captured session to verify.';

function Chip({ step }) {
  const st = STATE_STYLE[step.state] || STATE_STYLE.unknown;
  const usbViewable = step.id === 'viewable' && step.state === 'unknown';
  const tip = usbViewable
    ? (step.avEvaluated ? VIEWABLE_USB_TIP_AV : VIEWABLE_USB_TIP)
    : `${step.label}: ${STATE_WORD[step.state] || step.state}${step.why && step.why[0] ? ` - ${step.why[0]}` : ''}`;
  // Evidence-only steps (Viewability measured) never feed the verdict, so they are
  // OUTLINED (no fill) next to the filled chips of verdict-counted steps; a green
  // slot with an amber Viewable chip must not read as contradictory. The one exception is
  // a slot carrying activeview_not_viewable: slotStory sets evidenceOnly:false, so the chip fills.
  const outlined = !!step.evidenceOnly && !usbViewable;
  return (
    <span
      title={tip}
      data-state={step.state}
      data-evidence={step.evidenceOnly ? 'true' : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999,
        fontSize: 11, lineHeight: '16px', whiteSpace: 'nowrap',
        background: outlined ? 'transparent' : st.bg, color: st.fg, border: `1px ${st.dashed ? 'dashed' : 'solid'} ${st.bd}`,
      }}
    >
      <span aria-hidden="true" style={{ fontWeight: 700 }}>{st.icon}</span>
      <span style={{ fontWeight: 600 }}>{step.label}</span>
      {step.summary ? <span style={{ opacity: 0.85 }}>{step.summary}</span> : null}
    </span>
  );
}

const NOTE_COLOR = { warn: 'var(--warn-text)', fail: 'var(--error-text)', info: 'var(--muted)' };

/** Per-slot waterfall: each phase's request on one axis, 0 = GAM request left. */
function Timeline({ lc, rowsByKey, onOpenRequest }) {
  const t = useMemo(() => buildSlotTimeline(lc, rowsByKey), [lc, rowsByKey]);
  if (!t.anchorKnown || t.entries.length < 2) return null;
  const lo = Math.min(0, ...t.entries.map((e) => e.start));
  const hi = Math.max(1, ...t.entries.map((e) => (e.end != null ? e.end : e.start)));
  const span = hi - lo || 1;
  const pct = (v) => `${((v - lo) / span) * 100}%`;
  return (
    <div data-testid="slot-timeline">
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)' }}>Timing (relative to the GAM request)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
        {t.entries.map((e) => (
          <div
            key={e.id}
            role={e.rowKey != null ? 'button' : undefined}
            tabIndex={e.rowKey != null ? 0 : undefined}
            title={e.rowKey != null ? 'Open request' : undefined}
            onClick={(ev) => { ev.stopPropagation(); if (e.rowKey != null) onOpenRequest(e.rowKey, lc); }}
            onKeyDown={(ev) => { if (e.rowKey != null && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); ev.stopPropagation(); onOpenRequest(e.rowKey, lc); } }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, cursor: e.rowKey != null ? 'pointer' : 'default' }}
          >
            <span style={{ width: 110, flexShrink: 0, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.label}</span>
            <span style={{ position: 'relative', flex: 1, height: 10, minWidth: 80, background: 'var(--surface)', borderRadius: 3 }}>
              <span style={{ position: 'absolute', left: pct(0), top: -2, bottom: -2, width: 1, background: 'var(--line)' }} />
              {e.end != null ? (
                <span style={{ position: 'absolute', left: pct(e.start), width: `max(2px, ${((e.end - e.start) / span) * 100}%)`, top: 1, bottom: 1, borderRadius: 2, background: e.id === 'gam' ? 'var(--primary)' : 'var(--ink-2)' }} />
              ) : (
                <span style={{ position: 'absolute', left: pct(e.start), top: 1, width: 8, height: 8, marginLeft: -4, borderRadius: '50%', background: e.id === 'gam' ? 'var(--primary)' : 'var(--ink-2)' }} />
              )}
            </span>
            <span style={{ width: 130, flexShrink: 0, fontFamily: 'var(--font-mono)', color: 'var(--muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>
              {e.start === 0 && e.id === 'gam' ? '0' : `${e.start > 0 ? '+' : ''}${Math.round(e.start)}`}
              {e.end != null ? ` → ${e.end > 0 ? '+' : ''}${Math.round(e.end)}` : ''} ms
            </span>
          </div>
        ))}
      </div>
      {t.notes.length ? (
        <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 12 }}>
          {t.notes.map((n, i) => <li key={i} style={{ color: NOTE_COLOR[n.level] || 'var(--ink)' }}>{n.msg}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

// Copy the slot as a Markdown ticket / download it as a slot-only HAR (WP2).
// Both are offline: they read the rows the browser already holds, no endpoint.
export const HAR_USB_TIP = 'Request-only: the HAR has no responses or bodies';
const ACTION_BTN = { fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--primary)', cursor: 'pointer' };

function SlotActions({ lc, story, rowsByKey, deviceId, harId, onOpenSummary }) {
  const [note, setNote] = useState('');
  const usb = isLogcatLifecycle(lc);
  const missing = useMemo(() => {
    let n = 0;
    for (const k of slotRowKeys(lc)) {
      const hit = rowsByKey && typeof rowsByKey.get === 'function' ? rowsByKey.get(k) : null;
      if (!hit) n += 1;
    }
    return n;
  }, [lc, rowsByKey]);
  const copyTicket = () => {
    const text = buildSlotTicket(story, lc, {
      origin: typeof window !== 'undefined' ? window.location.origin : '',
      deviceId: deviceId || undefined,
      harId: harId || undefined,
      device: deviceId || undefined,
      rowsByKey,
    });
    setNote(copyTextToClipboard(text) ? 'Ticket copied' : 'Copy failed - select and copy manually');
  };
  // Lookup keys (correlator, ad unit, hb_*, amzn_b) for GAM Delivery Inspector.
  const copyKeys = () => {
    // Prefer the captured row: server lifecycles can carry a projected GAM row without the URL.
    const gam = (lc.phases && lc.phases.gam) || {};
    const live = gam.rowKey != null && rowsByKey && typeof rowsByKey.get === 'function' ? rowsByKey.get(gam.rowKey) : null;
    const { text } = buildDisplayLookupKeys(live ? { ...lc, phases: { ...lc.phases, gam: { ...gam, row: live } } } : lc);
    setNote(text && copyTextToClipboard(text) ? 'GAM keys copied' : 'Copy failed - select and copy manually');
  };
  const downloadHar = () => {
    const { har, filename } = buildSlotHar(lc, rowsByKey);
    downloadHarFile(har, filename);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} data-testid="slot-actions">
      <button type="button" onClick={copyTicket} style={ACTION_BTN}>Copy ticket</button>
      <button type="button" onClick={copyKeys} title="Copy lookup keys (correlator, ad unit, hb_*, amzn_b) to paste into GAM Delivery Inspector" style={ACTION_BTN}>Copy GAM keys</button>
      {onOpenSummary ? (
        <button type="button" onClick={() => onOpenSummary(lc)} title="Open the slot summary in the side panel (Export Incident Report is there)" style={ACTION_BTN}>Open summary</button>
      ) : null}
      <button type="button" onClick={downloadHar} title={usb ? HAR_USB_TIP : undefined} style={ACTION_BTN}>
        {missing > 0 ? `Download HAR (${missing} request(s) no longer in memory)` : 'Download HAR'}
      </button>
      {usb ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>{HAR_USB_TIP}</span> : null}
      {note ? <span style={{ fontSize: 11, color: 'var(--muted)' }} data-testid="slot-action-note">{note}</span> : null}
    </div>
  );
}

function SlotRow({ lc, story, open, onToggle, onOpenRequest, onOpenSummary, rowsByKey, deviceId, harId, ruleState = null }) {
  const confidence = lifecycleConfidence(lc.phases);
  const rewriteLines = open ? rewriteLinesForSlot(lc, rowsByKey) : [];
  const usbRewriteNotice = open ? logcatRewriteNotice(lc, rowsByKey, ruleState, lc.device || deviceId) : null;
  return (
    <div style={{ borderTop: '1px solid var(--line)' }} data-testid="slot-row" data-status={story.status} data-slot-key={String(story.key)}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 10px', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: DOT[story.status] || DOT.pass, flexShrink: 0 }} title={`Verdict: ${story.status}`} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={story.adUnit}>
            {story.adUnit || '?'}
          </span>
          {story.sizes && story.sizes.length ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>{[].concat(story.sizes).join(', ')}</span> : null}
          <RefreshTag lc={lc} />
          {story.redundancy && story.redundancy.length ? (
            <span
              data-testid="slot-redundancy"
              title={story.redundancy.map((r) => `${r.label}: ${r.detail}`).join('\n')}
              style={{ fontSize: 10, fontWeight: 700, padding: '0 6px', borderRadius: 4, ...REDUNDANCY_TONE[story.redundancy.reduce((w, r) => (rankOf(r.status) < rankOf(w) ? r.status : w), 'review')] }}
            >
              {story.redundancy.map((r) => r.label).join(' · ')}
            </span>
          ) : null}
          <FillFlag fill={story.fill} />
          {confidence && confidence !== 'exact' ? <ConfidenceChip level={confidence} style={{ flexShrink: 0 }} /> : null}
          {story.usb ? (
            <span title="Captured over USB (adb logcat): GAM response, Prebid and APS traffic are not visible" style={{ fontSize: 10, fontWeight: 700, padding: '0 6px', borderRadius: 4, background: 'var(--neutral-bg)', color: 'var(--neutral-text)', border: '1px solid var(--neutral-border)' }}>USB</span>
          ) : null}
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }} title="Time the GAM request was sent" data-testid="slot-time">
            {fmtClock(lc.timestamp)}
          </span>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{open ? '▾' : '▸'}</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {story.steps.map((s) => <Chip key={s.id} step={s} />)}
        </div>
        <div style={{ fontSize: 12, color: story.status === 'pass' ? 'var(--ink-2)' : 'var(--ink)' }} data-testid="slot-verdict">{story.verdict}</div>
      </div>
      {open ? (
        <div style={{ padding: '4px 10px 10px 27px', display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="slot-detail">
          <SlotActions lc={lc} story={story} rowsByKey={rowsByKey} deviceId={lc.device || deviceId} harId={harId} onOpenSummary={onOpenSummary} />
          <LinkConfidenceRow phases={lc.phases} />
          <MediationBuyers mediation={lc.phases && lc.phases.mediation} />
          {story.fill ? (
            <div data-testid="slot-fill-why">
              <div style={{ fontSize: 11, fontWeight: 600, color: (FILL_STYLE[story.fill.state] || FILL_STYLE.unknown).fg }}>
                {story.fill.state === 'nofill' ? 'Why no fill' : 'Fill'}
              </div>
              <ul style={{ margin: '2px 0 0', paddingLeft: 16, fontSize: 12, color: 'var(--ink)' }}>
                {story.fill.why.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          ) : null}
          {story.slotWhy.length ? (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)' }}>Slot</div>
              <ul style={{ margin: '2px 0 0', paddingLeft: 16, fontSize: 12, color: 'var(--ink)' }}>
                {story.slotWhy.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          ) : null}
          {story.steps.map((s) => (
            <div key={s.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 160, flexShrink: 0 }}>
                <Chip step={{ ...s, summary: '' }} />
                {s.lowConfidence ? (
                  <span
                    data-testid="low-confidence-chip"
                    title="Matched by time window only; a concurrent slot may own this beacon"
                    style={{ display: 'inline-block', marginTop: 4, padding: '1px 6px', borderRadius: 999, fontSize: 10, lineHeight: '14px', border: '1px dashed var(--warn-text)', color: 'var(--warn-text)' }}
                  >
                    low confidence (time-window match)
                  </span>
                ) : null}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink)', flex: 1, minWidth: 0 }}>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {s.why.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
                {s.id === 'gam' && rewriteLines.length ? (
                  <div style={{ marginTop: 4 }} data-testid="slot-rewrite">
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)' }}>{REWRITE_LABEL}</div>
                    <ul style={{ margin: '2px 0 0', paddingLeft: 16, color: 'var(--ink)' }}>
                      {rewriteLines.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                ) : null}
                {s.id === 'gam' && usbRewriteNotice ? (
                  <div
                    style={{ marginTop: 4, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--line)', background: 'var(--neutral-bg)', color: 'var(--muted)' }}
                    data-testid="slot-rewrite-usb"
                  >
                    {usbRewriteNotice}
                  </div>
                ) : null}
              </div>
              {s.id === 'viewable' && Array.isArray(s.rowKeys) && s.rowKeys.length ? (
                // Viewable: one entry per ActiveView ping / OMID beacon the step was built from
                // (also when the step is counted, i.e. evidenceOnly:false).
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 360, flexShrink: 0 }} data-testid="viewable-beacons">
                  {s.rowKeys.map((k) => {
                    const beacon = rowsByKey && typeof rowsByKey.get === 'function' ? rowsByKey.get(k) : null;
                    const url = (beacon && beacon.url) || '';
                    return (
                      <div key={String(k)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span title={url} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{url || String(k)}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onOpenRequest(k, lc); }}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--primary)', cursor: 'pointer', flexShrink: 0 }}
                        >
                          Open request
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : s.rowKey != null ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenRequest(s.rowKey, lc); }}
                  style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--primary)', cursor: 'pointer', flexShrink: 0 }}
                >
                  Open request
                </button>
              ) : null}
            </div>
          ))}
          <Timeline lc={lc} rowsByKey={rowsByKey} onOpenRequest={onOpenRequest} />
        </div>
      ) : null}
    </div>
  );
}

function SummaryLine({ summary, usbCount, total, source, redundancies, orphanReport, onJumpTo }) {
  const pct = summary.fillRate == null ? null : Math.round(summary.fillRate * 100);
  const fill = pct != null ? `fill ${pct}%${summary.fillKnown < summary.slots ? ` (of ${summary.fillKnown} known)` : ''}` : (usbCount ? 'fill: n/a (USB)' : 'fill: n/a');
  const capture = usbCount === 0 ? 'MITM' : usbCount === total ? 'USB' : `MITM + USB (${usbCount})`;
  const bits = [
    `${summary.slots} slot${summary.slots === 1 ? '' : 's'}`,
    fill,
    `${summary.failing} failing`,
    `${summary.warning} warning`,
    summary.bidders.length ? `bidders: ${summary.bidders.join(', ')}` : null,
    `capture: ${capture}`,
  ].filter(Boolean);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 10px', fontSize: 12, color: 'var(--ink)' }} data-testid="slot-summary">
      <span style={{ fontWeight: 600 }}>{bits.join(' · ')}</span>
      {redundancies && redundancies.length ? <RedundancyPopover findings={redundancies} onJumpTo={onJumpTo} /> : null}
      {orphanReport ? <OrphanPopover report={orphanReport} onJumpTo={onJumpTo} /> : null}
      <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted)' }} data-testid="slot-source">
        {source === 'server' ? 'server' : source === 'local' ? 'local fallback' : 'loading…'}
      </span>
    </div>
  );
}

const toMsTs = (v) => (typeof v === 'number' ? v : Date.parse(v) || 0);

/** Page visits for the Overview timeline dividers: one per page bucket, by first slot time. */
export function visitsOf(pages, allMode) {
  return pages
    .map((p) => {
      const start = Math.min(...p.stories.map((x) => toMsTs(x.lc.timestamp)).filter((t) => t > 0));
      return {
        key: p.key,
        start,
        label: `${allMode && p.device ? `${p.device} · ` : ''}${shortPage(p.page)}`,
        page: p.page || null,
        visitLabel: p.visitLabel || null,
        slotKeys: p.stories.map((x) => x.story.key),
      };
    })
    .filter((v) => Number.isFinite(v.start))
    .sort((a, b) => a.start - b.start);
}

/**
 * mode 'slots'    — the per-slot list (default tab).
 * mode 'overview' — the session charts (Overview tab); onShowSlot(key) asks the
 *                   parent to switch to Slots and open that slot (focusSlot).
 */
// Empty Slots, explained: what WAS captured, and the likeliest reason there's no GAM yet.
function NoGamYet({ rows }) {
  const d = diagnoseNoGam(rows);
  return (
    <div style={{ padding: 24, display: 'grid', gap: 10, maxWidth: 760 }}>
      <div style={{ color: 'var(--ink)', fontSize: 14, fontWeight: 600 }}>
        No GAM ad requests yet — {d.total} request{d.total === 1 ? '' : 's'} captured{d.demand ? `, ${d.demand} header-bidding` : ''}.
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.5 }}>{NO_GAM_ADVICE[d.cause]}</div>
      {d.topHosts.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          <div style={{ marginBottom: 4 }}>Captured so far (see the Requests tab for all of them):</div>
          {d.topHosts.map((h) => (
            <div key={h.host} style={{ fontFamily: 'var(--font-mono)' }}>{h.count} × {h.host}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export const SLOT_EXPIRED_COPY = 'This slot is not in the current session. Slot links work while the session is open.';

export default function SlotStoryView({ allRows = [], allDevicesRows = null, onJumpTo, mode = 'slots', onShowSlot, focusSlot = null, onFocusResolved = null, deviceId = null, harId = null }) {
  const deviceCount = useMemo(
    () => (Array.isArray(allDevicesRows) ? new Set(allDevicesRows.map((r) => r && r._deviceTab)).size : 0),
    [allDevicesRows]
  );
  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem('slots.scope') === 'all' ? 'all' : 'device'; } catch { return 'device'; }
  });
  const chooseScope = (v) => { setScope(v); try { localStorage.setItem('slots.scope', v); } catch { /* private mode */ } };
  // Newest first (default) or oldest first, for visits and the slots inside them. Remembered per browser.
  const [order, setOrder] = useState(() => {
    try { return localStorage.getItem('slots.order') === 'oldest' ? 'oldest' : 'newest'; } catch { return 'newest'; }
  });
  const chooseOrder = (v) => { setOrder(v); try { localStorage.setItem('slots.order', v); } catch { /* private mode */ } };
  const allMode = scope === 'all' && deviceCount > 1;
  const sourceRows = allMode ? allDevicesRows : allRows;
  const [serverGraph, setServerGraph] = useState(null);
  const [graphSource, setGraphSource] = useState('pending');
  const [expanded, setExpanded] = useState(() => new Set());
  const [groupOpen, setGroupOpen] = useState({});
  const latest = useLatestWins();
  // WP-A5 — per-screen expectation misses. Only fetched when an App Pack WITH screens
  // matches this session's GAM IUs; otherwise nothing is requested or shown.
  const [screenResults, setScreenResults] = useState(null);
  const screenPackSlug = useMemo(() => {
    try {
      for (const r of Array.isArray(sourceRows) ? sourceRows : []) {
        const t = String((r && (r.requestType || r.type)) || '').toLowerCase();
        if (t !== 'google' && t !== 'gam') continue;
        const vd = (r && r.validData) || {};
        let iu = vd.adUnit || '';
        if (!iu) { try { iu = new URL(r.url).searchParams.get('iu') || ''; } catch { iu = ''; } }
        const pack = iu ? findPackByIu(iu) : null;
        if (!pack) continue;
        const entry = Object.entries(getPacks() || {}).find(([, p]) => p === pack);
        const slug = (entry && entry[0]) || pack.slug;
        return slug && Array.isArray(pack.screens) && pack.screens.length ? slug : null;
      }
    } catch { /* no pack */ }
    return null;
  }, [sourceRows]);
  const sourceRowCount = Array.isArray(sourceRows) ? sourceRows.length : 0;
  useEffect(() => {
    if (!screenPackSlug || mode !== 'overview') { setScreenResults(null); return undefined; }
    let live = true;
    runChecks({ suite: 'adtech-baseline', app: screenPackSlug, rows: sourceRows, record: false })
      .then((r) => { if (live) setScreenResults(r && Array.isArray(r.results) ? r.results : null); })
      .catch(() => { if (live) setScreenResults(null); });
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenPackSlug, mode, sourceRowCount]);
  const rowsByKey = useMemo(() => {
    const m = new Map();
    for (const r of sourceRows) if (r && r._k != null) m.set(r._k, r);
    return m;
  }, [sourceRows]);
  // IU Rewrite rule state is only needed for the USB notice, so the WS opens only while a logcat row is present.
  const hasLogcatGam = useMemo(
    () => (Array.isArray(sourceRows) ? sourceRows : []).some((r) => r && r.captureSource === 'logcat'),
    [sourceRows]
  );
  const ruleState = useIuRules(hasLogcatGam);

  const localCache = useRef({ rows: null, value: null });
  const buildLocal = () => {
    if (localCache.current.rows === sourceRows) return localCache.current.value;
    const value = allMode
      ? [...splitByDevice(sourceRows)].flatMap(([d, rows]) => buildSlotLifecycles(rows, {}).map((lc) => tagDevice(lc, d)))
      : buildSlotLifecycles(sourceRows, {});
    localCache.current = { rows: sourceRows, value };
    return value;
  };

  useEffect(() => {
    if (sourceRows.length) return;
    latest.invalidate();
    setServerGraph(null);
    setGraphSource('empty');
  }, [sourceRows]); // eslint-disable-line react-hooks/exhaustive-deps
  // One POST in flight, at most every few seconds; an in-flight answer still lands.
  useCoalescedPost(() => {
      const seq = latest.begin();
      const req = allMode
        ? Promise.all([...splitByDevice(sourceRows)].map(([d, rows]) => postLifecycleGroups(rows, { manualOverrides: {} }).then((g) => [d, g])))
          .then(mergeDeviceGraphs)
        : postLifecycleGroups(sourceRows, { manualOverrides: {} });
      return req
        .then((res) => {
          if (!latest.accept(seq)) return;
          setServerGraph(res);
          setGraphSource('server');
        })
        .catch(() => {
          if (!latest.accept(seq)) return;
          setServerGraph(null);
          setGraphSource('local');
        });
  }, [sourceRows], { enabled: sourceRows.length > 0, urgentDeps: [allMode] });

  const resolved = useMemo(
    () => resolveGraphSource(serverGraph, graphSource, {
      lifecycles: buildLocal,
      groups: groupLifecyclesBySlot,
      // Per device, like the lifecycles: repeat detection groups by ad unit and must not mix phones.
      redundancies: (lcs) => (allMode
        ? [...splitByDevice(sourceRows)].flatMap(([d, rows]) => detectRedundancies(rows, lcs.filter((lc) => (lc.device || '') === d)))
        : detectRedundancies(sourceRows, lcs)),
      orphanReport: (lcs) => (allMode
        ? mergeOrphanReports([...splitByDevice(sourceRows)].map(([d, rows]) => buildOrphanReport(rows, lcs.filter((lc) => (lc.device || '') === d))))
        : buildOrphanReport(sourceRows, lcs)),
      omidSessions: () => (allMode
        ? [...splitByDevice(sourceRows)].flatMap(([, rows]) => reconstructOmidSessions(rows))
        : reconstructOmidSessions(sourceRows)),
    }),
    [serverGraph, graphSource, sourceRows] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { lifecycles, groups, orphanReport, omidSessions, usingServerOmid } = resolved;
  const gamCapture = useMemo(() => partialGamCapture(sourceRows), [sourceRows]);
  const redundancies = useMemo(() => resolved.redundancies || [], [resolved]);
  const summary = useMemo(() => summarizeSession(lifecycles, { redundancies }), [lifecycles, redundancies]);
  // Session rollup of ActiveView pings (attached vs not) from the rows and the lifecycles shown.
  const avRollup = useMemo(() => activeViewRollup(sourceRows, lifecycles), [sourceRows, lifecycles]);
  const pages = useMemo(() => bucketByPage(groups, rowsByKey, avRollup, redundancies), [groups, rowsByKey, avRollup, redundancies]);
  const shownPages = useMemo(() => (order === 'oldest'
    ? pages.slice().reverse().map((p) => ({ ...p, stories: p.stories.slice().reverse() }))
    : pages), [pages, order]);
  const newestLatest = useMemo(() => pages.reduce((m, p) => Math.max(m, p.latest || 0), 0), [pages]);
  const usbCount = useMemo(() => pages.reduce((n, p) => n + p.stories.filter((s) => s.story.usb).length, 0), [pages]);

  const toggleSlot = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Timeline mark -> open its page group, expand the slot, scroll to it.
  const pendingScroll = useRef(null);
  const selectSlot = (key) => {
    // String compare: a `?slot=` value is always text, lifecycle keys may be numeric `_k`.
    const page = pages.find((p) => p.stories.some((x) => String(x.story.key) === String(key)));
    if (!page) return false;
    const real = page.stories.find((x) => String(x.story.key) === String(key)).story.key;
    setGroupOpen((g) => ({ ...g, [page.key]: true }));
    setExpanded((prev) => new Set(prev).add(real));
    pendingScroll.current = real;
    return true;
  };
  // A link that does not resolve expands NOTHING and says so (never a wrong slot).
  const focusKey = focusSlot && focusSlot.key != null ? focusSlot.key : null;
  const [dismissedFocus, setDismissedFocus] = useState(null);
  useEffect(() => {
    if (focusKey != null && selectSlot(focusKey) && onFocusResolved) onFocusResolved(focusKey);
  }, [focusSlot, pages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const focusExpired = focusKey != null
    && (graphSource === 'server' || graphSource === 'local')
    && dismissedFocus !== focusSlot
    && !pages.some((p) => p.stories.some((x) => String(x.story.key) === String(focusKey)));
  const expiredBanner = focusExpired ? (
    <div
      role="alert"
      data-testid="slot-expired"
      style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 8px 0', padding: '6px 10px', fontSize: 12, borderRadius: 6, background: 'var(--warn-bg)', color: 'var(--warn-text)', border: '1px solid var(--warn-border)' }}
    >
      <span>{SLOT_EXPIRED_COPY}</span>
      <button type="button" onClick={() => setDismissedFocus(focusSlot)} style={{ marginLeft: 'auto', fontSize: 11, border: 'none', background: 'none', color: 'var(--warn-text)', cursor: 'pointer' }}>Dismiss</button>
    </div>
  ) : null;
  useEffect(() => {
    const key = pendingScroll.current;
    if (key == null) return;
    pendingScroll.current = null;
    const node = document.querySelector(`[data-slot-key="${String(key).replace(/"/g, '\\"')}"]`);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });

  const openRequest = (rowKey, lc) => {
    if (!onJumpTo || rowKey == null) return;
    // tab:'details' — open that step's request itself, not the slot summary.
    onJumpTo(rowKey, { adUnit: lc.adUnit, visitKey: lc.visitKey, rowKey, tab: 'details' }, lc);
  };

  const openSummary = onJumpTo ? (lc) => {
    const rowKey = (lc.phases && lc.phases.gam && lc.phases.gam.rowKey) != null ? lc.phases.gam.rowKey : lc.key;
    onJumpTo(rowKey, { adUnit: lc.adUnit, visitKey: lc.visitKey, rowKey, tab: 'summary' }, lc);
  } : null;

  const scopeBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 0', fontSize: 12, color: 'var(--ink-2)' }} data-testid="slot-scope">
      {deviceCount > 1 && <span>Show</span>}
      {deviceCount > 1 && <div role="group" aria-label="Device scope" style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden' }}>
        {[['device', 'This device'], ['all', `All devices (${deviceCount})`]].map(([v, label], i) => (
          <button
            key={v}
            type="button"
            aria-pressed={scope === v}
            onClick={() => chooseScope(v)}
            style={{ fontSize: 12, padding: '3px 10px', border: 'none', borderLeft: i ? '1px solid var(--line)' : 'none', cursor: 'pointer', background: scope === v ? 'var(--primary)' : 'transparent', color: scope === v ? 'white' : 'var(--ink-2)', fontWeight: scope === v ? 600 : 400 }}
          >
            {label}
          </button>
        ))}
      </div>}
      <div role="group" aria-label="Sort order" data-testid="slot-order" style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden', marginLeft: 'auto' }}>
        {[['newest', 'Newest first'], ['oldest', 'Oldest first']].map(([v, label], i) => (
          <button
            key={v}
            type="button"
            aria-pressed={order === v}
            onClick={() => chooseOrder(v)}
            style={{ fontSize: 12, padding: '3px 10px', border: 'none', borderLeft: i ? '1px solid var(--line)' : 'none', cursor: 'pointer', background: order === v ? 'var(--primary)' : 'transparent', color: order === v ? 'white' : 'var(--ink-2)', fontWeight: order === v ? 600 : 400 }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  if (!sourceRows.length) {
    return <div>{scopeBar}<div style={{ padding: 24, color: 'var(--muted)', fontSize: 13 }}>No traffic captured yet. Slots appear here as ad requests arrive.</div></div>;
  }
  if (!lifecycles.length) {
    return (
      <div>
        {scopeBar}
        {mode === 'slots' ? expiredBanner : null}
        <SummaryLine summary={summary} usbCount={0} total={0} source={resolved.usingServer ? 'server' : graphSource} />
        <NoGamYet rows={sourceRows} />
      </div>
    );
  }

  if (mode === 'overview') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {scopeBar}
        <SessionOverview lifecycles={lifecycles} visits={visitsOf(pages, allMode)} onShowSlot={onShowSlot || selectSlot}{...(screenResults ? { expectationMisses: buildExpectationMisses(screenResults) } : {})} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {scopeBar}
      {expiredBanner}
      <SummaryLine
        summary={summary}
        usbCount={usbCount}
        total={lifecycles.length}
        source={resolved.usingServer ? 'server' : graphSource}
        redundancies={redundancies}
        orphanReport={orphanReport}
        onJumpTo={onJumpTo ? (rowKey) => onJumpTo(rowKey, { rowKey, tab: 'details' }) : undefined}
      />
      <div style={{ margin: '0 8px' }}>
        {gamCapture.partial && (
          <div role="status" data-testid="partial-gam" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', color: 'var(--warn-text)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12, lineHeight: 1.5 }}>
            <span style={{ flexShrink: 0 }}>⚡</span>
            <span>
              <strong>Partial GAM capture:</strong> {gamCapture.gam} GAM request{gamCapture.gam !== 1 ? 's' : ''} for ~{gamCapture.prebid} Prebid auction{gamCapture.prebid !== 1 ? 's' : ''}, so some slots are probably missing here.
              {' '}The device likely sent part of its GAM traffic over QUIC (HTTP/3), which bypasses the proxy. Set BLOCK_QUIC=true in the proxy .env and restart the app.
            </span>
          </div>
        )}
        <SessionQualityAlert lifecycles={lifecycles} onSelect={(lc) => selectSlot(lc.key)} hint="Click a slot to open it; Copy ticket in the slot gives the full evidence." />
        <OmidSessionsPanel sessions={omidSessions} local={!usingServerOmid} />
      </div>
      {shownPages.map((p) => {
        // the newest page is open by default (whatever the sort order) so the latest request is visible
        const open = groupOpen[p.key] != null ? groupOpen[p.key] : (p.latest === newestLatest || p.status !== 'pass');
        const counts = p.stories.reduce((c, s) => { c[s.story.status] = (c[s.story.status] || 0) + 1; return c; }, {});
        return (
          <div key={p.key} style={{ border: '1px solid var(--line)', borderRadius: 8, margin: '0 8px 8px', background: 'var(--card)', overflow: 'hidden' }} data-testid="slot-group" data-status={p.status}>
            <div
              role="button"
              tabIndex={0}
              aria-expanded={open}
              onClick={() => setGroupOpen((g) => ({ ...g, [p.key]: !open }))}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGroupOpen((g) => ({ ...g, [p.key]: !open })); } }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', background: 'var(--surface)' }}
            >
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: DOT[p.status] || DOT.pass }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.page || ''}>
                {allMode && p.device ? <span style={{ fontWeight: 700, color: 'var(--ink-2)' }}>{p.device} · </span> : null}
                {p.page || 'Session'}{p.visitLabel ? ` · ${p.visitLabel}` : ''}
              </span>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                {p.stories.length} slot{p.stories.length === 1 ? '' : 's'}
                {counts.fail ? ` · ${counts.fail} failing` : ''}{counts.warn ? ` · ${counts.warn} warning` : ''}
              </span>
              <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }} title="Latest GAM request on this page">
                {p.latest ? `latest ${fmtClock(p.latest)}` : ''}
              </span>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>{open ? '▾' : '▸'}</span>
            </div>
            {open ? p.stories.map(({ lc, story }) => (
              <SlotRow
                key={story.key}
                lc={lc}
                story={story}
                open={expanded.has(story.key)}
                onToggle={() => toggleSlot(story.key)}
                onOpenRequest={openRequest}
                onOpenSummary={openSummary}
                rowsByKey={rowsByKey}
                deviceId={deviceId}
                harId={harId}
                ruleState={ruleState}
              />
            )) : null}
          </div>
        );
      })}
      <div style={{ margin: '0 8px 8px' }}>
        <SDKPanel rows={sourceRows} />
      </div>
    </div>
  );
}
